import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import {
  QuestionsGrid,
  QuestionButton,
  PageNumberBadge,
  QuestionBookmarkIcon
} from 'common/common-styles'
import { theme } from 'config/theme'
import { MainTocItem } from 'interfaces/assignments'
import { ReactComponent as NotesIcon } from 'assets/icons/notes.svg'
import { ViewMode } from 'interfaces/app'
import ChevronIcon from 'components/ChevronIcon/ChevronIcon'
import { isSectionPage, isTeacherGuidePage } from 'pages/AssessmentV2/utils'
import { useAppDispatch } from 'app/hooks'
import { setAssignmentPageId } from 'app/reducer/assignmentSlice'
import { ModalOverlay as SharedModalOverlay } from 'components/modals/styles'
import { ReactComponent as BookmarkIconFilled } from 'assets/img/bookmark-icon-filled.svg'
const Overlay = styled(SharedModalOverlay)`
  padding: 1rem;
`

const PopupCard = styled.div`
  background-color: ${({ theme }) => theme.colors.card.background};
  border-radius: 8px;
  width: 100%;
  max-width: 687px;
  padding: 29px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1.25rem 1.25rem 1.75rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 1rem 1rem 1.5rem;
  }
`

// ── Header ────────────────────────────────────────────────────────────────────

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const PopupTitle = styled.h2`
  font-family: ${(props) => props.theme.typography.fontPlusJakartaSans};
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text.primary};
  margin: 0;
  line-height: 1.3;
`

// ── Toggle Bar ────────────────────────────────────────────────────────────────
const LegendRow = styled.div`
  margin-top: 25px;
  margin-left: 20px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
`

const LegendText = styled.span`
  color: ${(props) => props.theme.colors.text.legend};
  font-family: ${(props) => props.theme.typography.fontInter};
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
`

const AnsweredDot = styled.span<{ color?: string }>`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: ${(props) => props.color || props.theme.colors.surface.error};
  display: inline-block;
`

// ── Inner Container ───────────────────────────────────────────────────────────

const InnerContainer = styled.div`
  background: ${({ theme }) => theme.colors.card.surface};
  /* border: 1px solid ${({ theme }) => theme.colors.border.primary}; */
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  padding: 12px 0;
`

// ── Column Headers ────────────────────────────────────────────────────────────

const ColumnHeaderRow = styled.div<{ $withTopPad?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $withTopPad }) => ($withTopPad ? '10px' : '0')}
    calc(14px + 1rem) 6px calc(14px + 1rem);
`

const ColumnHeaderLeft = styled.span`
  font-family: ${(props) => props.theme.typography.fontInter};
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.legend};
`

const ColumnHeaderRight = styled.span`
  font-family: ${(props) => props.theme.typography.fontInter};
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.legend};
  padding-right: 1.25rem;
`

// ── White Cards ───────────────────────────────────────────────────────────────

const WhiteCard = styled.div<{ $hasTopGap?: boolean }>`
  background: ${(props) => props.theme.colors.card.background};
  border-radius: 8px;
  /* border: 1px solid ${(props) => props.theme.colors.border.primary}; */
  margin: ${({ $hasTopGap }) => ($hasTopGap ? '4px 14px 0' : '0 14px 0')};
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
`

// ── Section Rows ──────────────────────────────────────────────────────────────

const SectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  border-bottom: 1px solid ${(props) => props.theme.colors.border.primary};
  cursor: default;

  &:last-child {
    border-bottom: none;
  }
`

const SelectableSectionRow = styled(SectionRow)<{
  $clickable?: boolean
  $disabled?: boolean
}>`
  cursor: ${({ $clickable, $disabled }) =>
    $clickable && !$disabled ? 'pointer' : 'default'};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};

  &:hover {
    background: ${({ $clickable, $disabled, theme }) =>
      $clickable && !$disabled ? theme.colors.card.surface : 'transparent'};
  }
`

const SectionName = styled.span<{ $bold?: boolean; $highlight?: boolean }>`
  font-family: ${({ theme }) => theme.typography.fontNunito};
  font-size: 15px;
  font-weight: 400;
  display: flex;
  gap: 10px;
  align-items: center;
  color: ${(props) =>
    props.$highlight
      ? props.theme.colors.text.highlight
      : props.theme.colors.text.primary};
  flex: 1;
`

const SectionWithSetRow = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 38px;
  border-bottom: 1px solid ${(props) => props.theme.colors.border.primary};

  > div:first-child {
    border-bottom: none;
  }

  &:last-child {
    border-bottom: none;
  }
`

const CurrentDash = styled.span`
  width: 14px;
  height: 2px;
  background: ${(props) => props.theme.colors.border.primary};
  display: inline-block;
`

const VerticalDash = styled.span`
  width: 2px;
  height: 17px;
  background: ${(props) => props.theme.colors.border.primary};
  display: inline-block;
`

const RowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: auto;
`

const ChevronAction = styled.span<{ $disabled?: boolean }>`
  display: inline-flex;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`

const PageNumber = styled.span`
  font-family: ${({ theme }) => theme.typography.fontNunito};
  font-size: 15px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.legend};
  min-width: 24px;
  text-align: right;
`

interface AssignmentTocModalProps {
  onClose: () => void
  onSelectReferenceSection?: (pageNumber: number) => void
  onSelectQuestion: (
    pageId: number,
    questionNumber: number,
    questionIndexInPage: number
  ) => void
  viewMode: ViewMode
  main: MainTocItem[]
  assessmentName: string
  currentPageId?: number
  visible?: boolean
  mode?: 'toc' | 'reference_section'
  disableChevronNavigation?: boolean
}

const AssignmentTocModal: React.FC<AssignmentTocModalProps> = ({
  onClose,
  onSelectReferenceSection,
  onSelectQuestion,
  main,
  assessmentName,
  visible,
  currentPageId,
  viewMode,
  mode = 'toc',
  disableChevronNavigation = false
}) => {
  const dispatch = useAppDispatch()
  const popupCardRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!visible) return

    /**
     * Focus the first control in the assignment question selector when it
     * opens, then return focus to the opener when it closes.
     */
    const previouslyFocusedElement = document.activeElement as HTMLElement
    popupCardRef.current
      ?.querySelector<HTMLElement>('[tabindex="0"], button:not([disabled])')
      ?.focus()

    return () => previouslyFocusedElement?.focus()
  }, [visible])

  useEffect(() => {
    if (!visible) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [visible, onClose])

  if (!visible) return null

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const isStudentMode = viewMode === ViewMode.Student
  const isReferenceSectionMode = mode === 'reference_section'

  const modalTitle = isReferenceSectionMode
    ? 'Add Reference Section'
    : assessmentName

  /**
   * TOC Modal is divided into two main sections:
   * 1. This section will display the teacher guide sections, question sets and sections
   * 2. This section will display the individual questions that are not part of any question set
   */
  const tocRowData = main?.filter((item) => {
    /**
     * Students aren't supposed to see the teacher sections,
     * similarly there will be student only sections that only a student view can see
     */

    if (isStudentMode) {
      return item.type !== 'teacher_guide_section'
    }

    return item
  })

  const tocHasPageDivider = tocRowData?.some(
    (item) => Number(item.page_questions?.length) > 1
  )

  const tocHasLinkedPages = tocRowData?.some(
    (item) => item.pages && item.pages.length > 1
  )

  const tocHasBookmarks = tocRowData?.some(
    (item) =>
      item?.pages?.some((page) => page.is_bookmarked) ||
      item?.page_questions?.some((pq) => pq.is_bookmarked)
  )

  const tocHasPageQuestions = tocRowData?.some(
    (item) => item.page_questions && item.page_questions.length > 0
  )

  const tocHasNotes = tocRowData.some(
    (item) =>
      item.pages?.some((page) => page.has_notes) ||
      item.page_questions?.some((pq) => pq.has_notes)
  )

  const onRowNavigate = (row: any) => {
    const firstPageId = row.pages?.[0]?.id

    if (isReferenceSectionMode) {
      // only sections are allowed to be selected as reference sections
      onSelectReferenceSection?.(firstPageId)
      return
    }

    dispatch(setAssignmentPageId(firstPageId))
    onClose()
  }

  function isPageBookmarked(pageId: number) {
    return tocRowData?.some(
      (item) =>
        item.pages?.some((page) => page.id === pageId && page.is_bookmarked) ||
        item.page_questions?.some(
          (pq) => pq.page_id === pageId && pq.is_bookmarked
        )
    )
  }

  function pageHasNotes(pageId: number) {
    return tocRowData?.some(
      (item) =>
        item.pages?.some((page) => page.id === pageId && page.has_notes) ||
        item.page_questions?.some((pq) => pq.page_id === pageId && pq.has_notes)
    )
  }

  const onPageNavigate = (page: any) => {
    dispatch(setAssignmentPageId(page.id))
    onClose()
  }

  const onQuestionNavigate = (
    pageId: number,
    questionNumber: number,
    questionIndexInPage: number
  ) => {
    if (isReferenceSectionMode) {
      return
    }

    onSelectQuestion(pageId, questionNumber, questionIndexInPage)
    onClose()
  }

  return (
    <Overlay
      onClick={handleOverlayClick}
      role='dialog'
      aria-modal='true'
      aria-labelledby='assignment-toc-modal-title'
    >
      <PopupCard ref={popupCardRef}>
        <PopupHeader>
          <ChevronIcon
            direction='left'
            size='md'
            onClick={onClose}
            ariaLabel='Close lesson navigation'
          />
          <PopupTitle id='assignment-toc-modal-title'>{modalTitle}</PopupTitle>
        </PopupHeader>

        <LegendRow>
          <LegendItem style={{ alignItems: 'flex-end' }}>
            <AnsweredDot color={theme.colors.surface.info} />
            <CurrentDash />
            <LegendText>Current</LegendText>
          </LegendItem>

          {tocHasPageDivider && (
            <LegendItem>
              <VerticalDash />
              <LegendText>Page Divider</LegendText>
            </LegendItem>
          )}
          {tocHasLinkedPages && (
            <LegendItem>
              <PageNumberBadge style={{ height: 17, width: 13 }} />
              <LegendText>Page</LegendText>
            </LegendItem>
          )}
          {tocHasPageQuestions && (
            <LegendItem>
              <QuestionButton style={{ height: 14, width: 14 }} />
              <LegendText>Question</LegendText>
            </LegendItem>
          )}
          {tocHasBookmarks && (
            <LegendItem>
              <BookmarkIconFilled style={{ height: 14, width: 14 }} />
              <LegendText>Bookmark</LegendText>
            </LegendItem>
          )}
          {tocHasNotes && (
            <LegendItem>
              <NotesIcon style={{ height: 18, width: 15 }} />
              <LegendText>Notes</LegendText>
            </LegendItem>
          )}
        </LegendRow>
        <InnerContainer>
          <ColumnHeaderRow>
            <ColumnHeaderLeft>Section or Question Set</ColumnHeaderLeft>
            <ColumnHeaderRight>
              {isStudentMode ? 'Page(s)' : 'Student Page(s)'}
            </ColumnHeaderRight>
          </ColumnHeaderRow>

          <WhiteCard>
            {tocRowData.map((item, idx) => {
              const { page_questions, pages } = item || {}
              const isTeacherGuide = isTeacherGuidePage(item.type)
              const isSection = isSectionPage(item.type)
              const notLastItem = idx < tocRowData.length - 1

              const isIndividual = item.type === 'INDIVIDUAL_QUESTIONS'

              const hasPageQuestions =
                page_questions &&
                page_questions.length > 0 &&
                page_questions.some((pq) => pq.question_sets?.length > 0)

              const hasLinkedPages =
                pages?.length > 1 && (isTeacherGuide || isSection)

              const linkedPages = hasLinkedPages ? item.pages : []
              const firstPage = pages?.[0]
              const pageNumbers = item.student_page_range
              const isCurrentRow =
                firstPage?.id === currentPageId && !hasLinkedPages

              return (
                <SectionWithSetRow
                  key={idx}
                  style={{
                    paddingBottom:
                      (hasPageQuestions || hasLinkedPages) && notLastItem
                        ? 8
                        : 0
                  }}
                >
                  <SelectableSectionRow
                    $clickable
                    $disabled={
                      isReferenceSectionMode &&
                      (item.type !== 'section' || isCurrentRow)
                    }
                    onClick={() => onRowNavigate(item)}
                  >
                    {isCurrentRow && (
                      <AnsweredDot color={theme.colors.surface.info} />
                    )}

                    <SectionName $highlight={isTeacherGuide}>
                      {/* 
                        This is for the row of the toc modal where all section names are presented,
                        for individual question set rows, we dont show the bookmark icon beside the name
                        we show it on each question button.
                      */}
                      {isPageBookmarked(firstPage?.id) && !isIndividual && (
                        <BookmarkIconFilled style={{ height: 14, width: 14 }} />
                      )}

                      {item.title}
                      {firstPage?.has_notes && (
                        <NotesIcon
                          style={{
                            width: 20,
                            height: 24
                          }}
                        />
                      )}
                    </SectionName>
                    <RowRight>
                      <PageNumber>{pageNumbers}</PageNumber>
                      {!isReferenceSectionMode && (
                        <ChevronAction $disabled={disableChevronNavigation}>
                          <ChevronIcon direction='right' size='md' />
                        </ChevronAction>
                      )}
                    </RowRight>
                  </SelectableSectionRow>
                  <div
                    style={{
                      width: '88%',
                      opacity: isReferenceSectionMode ? 0.5 : 1
                    }}
                  >
                    <QuestionsGrid>
                      {linkedPages?.map((linkedPage, linkedPageIdx) => {
                        const isCurrentPage = linkedPage.id === currentPageId
                        return (
                          <PageNumberBadge
                            $isTeacherGuide={isTeacherGuide}
                            key={linkedPageIdx}
                            $current={isCurrentPage}
                            onClick={
                              isReferenceSectionMode
                                ? undefined
                                : () => onPageNavigate(linkedPage)
                            }
                            style={{
                              cursor: isReferenceSectionMode
                                ? 'default'
                                : 'pointer'
                            }}
                          >
                            {linkedPage.label}
                          </PageNumberBadge>
                        )
                      })}
                      {page_questions?.map((questionPage, questionPageIdx) => {
                        const isCurrentPage =
                          questionPage.page_id === currentPageId &&
                          !isCurrentRow

                        const isFirstQuestionSetBookmarked =
                          isPageBookmarked(questionPage.page_id) &&
                          questionPageIdx === 0

                        const isFirstQuestionSetHasNotes =
                          pageHasNotes(questionPage.page_id) &&
                          questionPageIdx !== 0

                        return (
                          /**
                           *  A page of type = question_set may also have some questions, that is the intention here to show the user
                           *  which questions are inside a particular question set
                           * */
                          <React.Fragment key={questionPageIdx}>
                            {isCurrentPage && (
                              <span className='flex'>
                                <AnsweredDot
                                  color={theme.colors.surface.info}
                                />
                              </span>
                            )}

                            {/* This is the individual pages of question sets which are marked as bookmarked and question sets are linked, this icon shows up beside the question button */}
                            {!isFirstQuestionSetBookmarked && !isIndividual && (
                              <span className='flex'>
                                <BookmarkIconFilled
                                  style={{ height: 14, width: 14 }}
                                />
                              </span>
                            )}

                            {questionPage?.question_sets.map(
                              (questionSet, questionSetIdx) => (
                                <QuestionButton
                                  style={{
                                    cursor: isReferenceSectionMode
                                      ? 'default'
                                      : 'pointer'
                                  }}
                                  key={questionSet}
                                  onClick={() =>
                                    onQuestionNavigate(
                                      questionPage.page_id,
                                      questionSet,
                                      questionSetIdx
                                    )
                                  }
                                >
                                  {/* This is the bookmark icon for each question button that is only visible for individual question sets */}
                                  {isIndividual &&
                                    !isFirstQuestionSetBookmarked && (
                                      <QuestionBookmarkIcon />
                                    )}
                                  {questionSet}
                                </QuestionButton>
                              )
                            )}

                            {isFirstQuestionSetHasNotes && (
                              <span className='flex'>
                                <NotesIcon style={{ width: 20, height: 24 }} />
                              </span>
                            )}
                            {/* We dont show the question divider for individual questions, because there will be 
                            only one question on that specific page, so bunch of dividers after every question button is wierd */}
                            {questionPageIdx < page_questions.length - 1 &&
                              !isIndividual && (
                                <VerticalDash style={{ alignSelf: 'center' }} />
                              )}
                          </React.Fragment>
                        )
                      })}
                    </QuestionsGrid>
                  </div>
                </SectionWithSetRow>
              )
            })}
          </WhiteCard>
        </InnerContainer>
      </PopupCard>
    </Overlay>
  )
}

export default AssignmentTocModal
